import {
  Aggregate,
  ConditionalFormattingLimit,
  NumberWidgetTrend,
  TrendColorScheme,
  TrendResultStatus,
  WidgetTypes,
} from '@ardoq/api-types';
import { formatNumber, Locale } from '@ardoq/locale';
import { isRollingTrendRange } from '../utils';
import { currentDate, formatDateShort } from '@ardoq/date-time';
import { IconName } from '@ardoq/icons';
import { StatusType } from '@ardoq/status-ui';
import { sortBy } from 'lodash';
import { EmptyTrendUIProps, NumberWidgetProps, TrendUIProps } from './types';
import { NumberWidgetRenderData } from '../renderTypes';

const getTrendChangePercentage = (current: number, old: number): number => {
  if (old === 0) return old;
  return Math.round(((current - old) / old) * 100);
};

const getColorForLimit = (
  value: number,
  limits: Array<ConditionalFormattingLimit>
) => {
  const sortedLimits = sortBy(limits, ['above']).reverse();
  return sortedLimits.find(limit => value > limit.above)?.color;
};

const getTrendUIProps = (
  value: number,
  trend: NumberWidgetTrend,
  userLocale: Locale
): TrendUIProps | EmptyTrendUIProps | undefined => {
  if (isRollingTrendRange(trend)) {
    const period = trend.rolling.value;
    if (
      !trend.resultStatus || // may be missing when switching datasource
      trend.resultStatus === TrendResultStatus.MISSING ||
      // history not supported with filters
      trend.resultStatus === TrendResultStatus.GLOBAL_FILTERS_APPLIED
    )
      return {
        period,
        status: trend.resultStatus ?? TrendResultStatus.MISSING,
        isEmptyTrend: true,
      };

    const isExactTrend = trend.resultStatus === TrendResultStatus.EXACT;
    const isUnchanged = value === trend.result.value;

    const tooltipText = `${formatDateShort(trend.result.date, userLocale)} - ${formatDateShort(currentDate(), userLocale)}`;

    if (isUnchanged)
      return {
        percentage: 0,
        isUnchanged,
        iconName: IconName.ARROW_FORWARD,
        period,
        tagStatusType: StatusType.INFO,
        tooltipText,
        isEmptyTrend: false,
        ...(isExactTrend
          ? { isExactTrend }
          : { isExactTrend, daysAgo: trend.result.daysAgo }),
      };

    const changePercentage = getTrendChangePercentage(
      value,
      trend.result.value
    );
    const percentage = Math.abs(changePercentage);
    const isHigherNow = value > trend.result.value;

    return {
      isUnchanged: false,
      isEmptyTrend: false,
      period,
      percentage,
      tooltipText,
      iconName: isHigherNow ? IconName.ARROW_UPWARD : IconName.ARROW_DOWNWARD,
      tagStatusType:
        trend.colorScheme === TrendColorScheme.CLASSIC
          ? isHigherNow
            ? StatusType.SUCCESS
            : StatusType.ERROR
          : isHigherNow
            ? StatusType.ERROR
            : StatusType.SUCCESS,
      ...(isExactTrend
        ? { isExactTrend }
        : { isExactTrend, daysAgo: trend.result.daysAgo }),
    };
  }
};

export const getNumberWidgetProps = (
  {
    data,
    aggregate,
    trend,
    isAbbreviated,
    numberFormatOptions,
    limits,
    resultLabel,
    viewType,
  }: NumberWidgetRenderData,
  userLocale: Locale
): NumberWidgetProps & { viewType: WidgetTypes.NUMBER } => {
  const formattedNumber = formatNumber(
    data,
    aggregate === Aggregate.COUNT || !numberFormatOptions
      ? undefined
      : numberFormatOptions,
    userLocale,
    isAbbreviated
  );
  const trendProps = trend
    ? getTrendUIProps(data, trend, userLocale)
    : undefined;

  const color = limits ? getColorForLimit(data, limits) : undefined;

  return {
    viewType,
    formattedNumber,
    color,
    trendProps,
    resultLabel: resultLabel ?? '',
  };
};
